import React, { createContext, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import {
  sidebarCollapseToggleSx,
  sidebarNavItemButtonSx,
} from '../constants/sidebar-nav';
import { APP_FONT_SIZE } from '../constants/typography';

const DEFAULT_COLLAPSED_WIDTH = 76;

interface SidebarContextValue {
  collapsed: boolean;
  /** Which drawer instance this subtree is rendered in. */
  drawerVariant: 'temporary' | 'permanent';
}

const SidebarContext = createContext<SidebarContextValue>({
  collapsed: false,
  drawerVariant: 'permanent',
});

const useSidebarContext = (): SidebarContextValue => useContext(SidebarContext);

export { useSidebarContext };

export interface SidebarProps {
  width: number;
  collapsedWidth?: number;
  collapsed?: boolean;
  mobileOpen: boolean;
  onMobileClose: () => void;
  children: React.ReactNode;
}

const SidebarRoot: React.FC<SidebarProps> = ({
  width,
  collapsedWidth = DEFAULT_COLLAPSED_WIDTH,
  collapsed = false,
  mobileOpen,
  onMobileClose,
  children,
}) => {
  const theme = useTheme();
  const desktopWidth = collapsed ? collapsedWidth : width;

  const widthTransition = theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  });

  const paperBaseSx = {
    boxSizing: 'border-box' as const,
    bgcolor: theme.palette.background.paper,
    borderRight: 'none',
    overflowX: 'hidden' as const,
  };

  return (
    <Box
      component="nav"
      sx={{
        width: { sm: desktopWidth },
        flexShrink: { sm: 0 },
        transition: { sm: widthTransition },
      }}
    >
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { ...paperBaseSx, width },
        }}
      >
        <SidebarContext.Provider value={{ collapsed: false, drawerVariant: 'temporary' }}>
          {children}
        </SidebarContext.Provider>
      </Drawer>

      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            ...paperBaseSx,
            width: desktopWidth,
            transition: widthTransition,
          },
        }}
      >
        <SidebarContext.Provider value={{ collapsed, drawerVariant: 'permanent' }}>
          {children}
        </SidebarContext.Provider>
      </Drawer>
    </Box>
  );
};

export interface SidebarHeaderProps {
  children: React.ReactNode;
  collapsedChildren?: React.ReactNode;
}

const SidebarHeader: React.FC<SidebarHeaderProps> = ({
  children,
  collapsedChildren,
}) => {
  const { collapsed } = useSidebarContext();

  return (
    <Toolbar
      sx={{
        px: 2,
        py: 3,
        overflow: 'hidden',
        justifyContent: collapsed ? 'center' : 'flex-start',
      }}
    >
      {collapsed ? collapsedChildren ?? children : children}
    </Toolbar>
  );
};

const SidebarExpandedOnly: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { collapsed } = useSidebarContext();
  return collapsed ? null : <>{children}</>;
};

const SidebarNav: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <List component="nav" disablePadding sx={{ py: 1.25 }}>
    {children}
  </List>
);

export interface SidebarNavGroupProps {
  label: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

const SidebarNavGroup: React.FC<SidebarNavGroupProps> = ({
  label,
  defaultOpen = true,
  children,
}) => {
  const theme = useTheme();
  const { collapsed } = useSidebarContext();
  const [open, setOpen] = useState(defaultOpen);

  if (collapsed) {
    return <Box sx={{ '& + &': { mt: 1 } }}>{children}</Box>;
  }

  return (
    <Box sx={{ mb: 1, '&:first-of-type': { mt: 0 }, mt: 1.5 }}>
      <ListItemButton
        onClick={() => setOpen((prev) => !prev)}
        disableRipple
        sx={{
          mx: 1.5,
          px: 1,
          py: 0.5,
          mb: 0.5,
          borderRadius: '10px',
          '&:hover': { background: 'transparent' },
        }}
      >
        <ListItemText
          primary={label}
          primaryTypographyProps={{
            variant: 'overline',
            sx: {
              fontWeight: 700,
              fontSize: '0.68rem',
              letterSpacing: '0.1em',
              color: theme.palette.text.secondary,
            },
          }}
        />
        {open ? (
          <ExpandLessIcon fontSize="small" sx={{ color: theme.palette.text.disabled }} />
        ) : (
          <ExpandMoreIcon fontSize="small" sx={{ color: theme.palette.text.disabled }} />
        )}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {children}
        </List>
      </Collapse>
    </Box>
  );
};

export interface SidebarNavItemProps {
  icon: React.ReactNode;
  label: string;
  selected?: boolean;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  /** Extra attributes on the clickable button (e.g. data-* for menu anchors). */
  buttonProps?: React.ButtonHTMLAttributes<HTMLDivElement> & {
    'data-account-menu-trigger'?: string;
  };
}

const SidebarNavItem: React.FC<SidebarNavItemProps> = ({
  icon,
  label,
  selected = false,
  onClick,
  buttonProps,
}) => {
  const theme = useTheme();
  const { collapsed } = useSidebarContext();

  const button = (
    <ListItemButton
      selected={selected}
      onClick={onClick}
      {...buttonProps}
      sx={sidebarNavItemButtonSx(theme, { collapsed })}
    >
      <ListItemIcon>{icon}</ListItemIcon>
      {!collapsed && (
        <ListItemText
          primary={label}
          primaryTypographyProps={{
            noWrap: true,
            sx: { fontSize: APP_FONT_SIZE.sm, fontWeight: selected ? 600 : 500 },
          }}
        />
      )}
    </ListItemButton>
  );

  return (
    <ListItem
      disablePadding
      sx={{ display: 'block', px: collapsed ? 0 : 1.5, mb: 0.5 }}
    >
      {collapsed ? (
        <Tooltip title={label} placement="right">
          {button}
        </Tooltip>
      ) : (
        button
      )}
    </ListItem>
  );
};

export interface SidebarCollapseToggleProps {
  onClick: () => void;
}

const SidebarCollapseToggle: React.FC<SidebarCollapseToggleProps> = ({
  onClick,
}) => {
  const theme = useTheme();
  const { collapsed } = useSidebarContext();
  const { t } = useTranslation();

  const expandLabel = t('sidebar.expandMenu', 'Expand menu');
  const collapseLabel = t('sidebar.collapseMenu', 'Collapse menu');
  const tooltipLabel = collapsed ? expandLabel : collapseLabel;

  return (
    <Box
      sx={{
        display: { xs: 'none', sm: 'flex' },
        justifyContent: collapsed ? 'center' : 'flex-end',
        px: 1.5,
        py: 1,
      }}
    >
      <Tooltip title={tooltipLabel} placement="right">
        <IconButton
          onClick={onClick}
          size="small"
          aria-label={tooltipLabel}
          sx={sidebarCollapseToggleSx(theme)}
        >
          {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export const Sidebar = Object.assign(SidebarRoot, {
  Header: SidebarHeader,
  ExpandedOnly: SidebarExpandedOnly,
  Nav: SidebarNav,
  NavGroup: SidebarNavGroup,
  NavItem: SidebarNavItem,
  CollapseToggle: SidebarCollapseToggle,
});

export default Sidebar;
